/* ============================================================
   Admin → Employees page — card grid + add/edit modal.
   Ported from design/admin2.jsx (EmployeesSection + EmployeeEditor).
   Live card preview side panel ports design/card.jsx Layout C.
   ============================================================ */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { I, Mark, initials } from "../../lib/brand";
import { SahabQR, qrSvgToPngBlob, qrSvgToSvgBlob } from "../../lib/SahabQR";
import {
  HttpError,
  type EmployeeAdmin,
  adminApi,
  shortCodeUrl,
  vcardUrl,
} from "../../lib/api";
import { whatsappLink } from "../../lib/employee";
import { safeBackgroundImage } from "../../lib/photoUrl";

const STR = {
  employees: "Employees",
  addEmployee: "Add employee",
  edit: "Edit",
  deactivate: "Deactivate",
  activate: "Activate",
  active: "ACTIVE",
  inactive: "INACTIVE",
  generateQr: "Generate QR",
  livePreview: "Live preview",
  clear: "Cancel",
  saveChanges: "Save changes",
  slug: "Slug",
  shortCode: "Short-code",
  name: "Full name",
  title: "Title",
  company: "Company",
  mobile: "Mobile",
  whatsapp: "WhatsApp",
  email: "Email",
  linkedin: "LinkedIn URL",
  photoUrl: "Photo URL (paste R2 public URL)",
  uploadPhoto: "Upload photo",
  prefilled: "WhatsApp prefilled text",
};

export default function AdminEmployees() {
  const [rows, setRows] = useState<EmployeeAdmin[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EmployeeAdmin | "new" | null>(null);

  const reload = () => {
    setError(null);
    adminApi
      .listEmployees(true)
      .then(setRows)
      .catch((e: unknown) => {
        setError(
          e instanceof HttpError
            ? `HTTP ${e.status}: ${e.message}`
            : e instanceof Error
              ? e.message
              : String(e),
        );
      });
  };

  useEffect(() => {
    reload();
  }, []);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <div className="tlm">
          {STR.employees} · {rows?.length ?? 0}
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => setEditing("new")}
        >
          {I.plus({ size: 16 })}
          {STR.addEmployee}
        </button>
      </div>

      {error && (
        <div
          style={{
            color: "#b91c1c",
            fontSize: 13,
            border: "1px solid #fecaca",
            background: "#fef2f2",
            borderRadius: "var(--r-sm)",
            padding: 10,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {rows === null ? (
        <div className="mono" style={{ fontSize: 12, color: "var(--text-3)" }}>
          Loading…
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 14,
          }}
        >
          {rows.map((e) => (
            <EmployeeCard
              key={e.id}
              row={e}
              onEdit={() => setEditing(e)}
              onToggle={async () => {
                const next = !e.active;
                try {
                  await adminApi.updateEmployee(e.id, { active: next });
                  reload();
                } catch (err) {
                  alert(String(err));
                }
              }}
            />
          ))}
        </div>
      )}

      {editing && (
        <EmployeeEditor
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </>
  );
}

/* ============================================================
   Card in the grid + QR download row (PNG / SVG per spec).
   ============================================================ */
const INVITE_STATUS_LABEL: Record<string, string> = {
  invited: "Invited",
  active: "Active login",
  disabled: "Login disabled",
};

function EmployeeCard({
  row,
  onEdit,
  onToggle,
}: {
  row: EmployeeAdmin;
  onEdit: () => void;
  onToggle: () => void;
}) {
  const qrRef = useRef<SVGSVGElement>(null);
  const qrUrl = shortCodeUrl(row.short_code);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  async function resendInvite() {
    setResending(true);
    try {
      await adminApi.resendInvite(row.id);
      setResent(true);
    } catch (err) {
      alert(String(err));
    } finally {
      setResending(false);
    }
  }

  async function downloadPNG() {
    if (!qrRef.current) return;
    const blob = await qrSvgToPngBlob(qrRef.current, 1024);
    triggerBlobDownload(blob, `${row.slug}-qr.png`);
  }
  function downloadSVG() {
    if (!qrRef.current) return;
    const blob = qrSvgToSvgBlob(qrRef.current);
    triggerBlobDownload(blob, `${row.slug}-qr.svg`);
  }

  return (
    <div
      className="emp-card"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        padding: 18,
        opacity: row.active ? 1 : 0.6,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <MiniAvatar row={row} size={52} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 15,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.full_name || "— invite pending —"}
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: "var(--text-2)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.title || row.company}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span
          className="mono"
          style={{ fontSize: 10, letterSpacing: ".14em", color: "var(--text-3)" }}
        >
          {row.short_code}
        </span>
        <span
          className="pill"
          style={{ color: row.active ? "var(--signal-ink)" : "var(--text-3)" }}
        >
          <span
            className="dot"
            style={{ background: row.active ? "var(--signal)" : "var(--text-3)" }}
          />
          {row.active ? STR.active : STR.inactive}
        </span>
      </div>
      {row.invite_status && (
        <div
          className="mono"
          style={{ fontSize: 11, color: "var(--text-3)", display: "flex", gap: 8, alignItems: "center" }}
        >
          <span
            className="pill"
            style={{
              color: row.invite_status === "active" ? "var(--signal-ink)" : "var(--text-3)",
            }}
          >
            <span
              className="dot"
              style={{
                background: row.invite_status === "active" ? "var(--signal)" : "var(--text-3)",
              }}
            />
            {INVITE_STATUS_LABEL[row.invite_status] ?? row.invite_status}
          </span>
        </div>
      )}
      {/* off-screen QR for export */}
      <div style={{ position: "absolute", left: -9999, top: -9999, pointerEvents: "none" }}>
        <SahabQR ref={qrRef} url={qrUrl} size={132} />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={{ flex: 1, minWidth: 90 }}
          onClick={onEdit}
        >
          {I.edit({ size: 15 })}
          {STR.edit}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            void downloadPNG();
          }}
          title="Download QR as PNG (1024×1024, per spec)"
        >
          {I.qr({ size: 15 })}PNG
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={downloadSVG}
          title="Download QR as SVG"
        >
          SVG
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={onToggle}
          style={{ color: row.active ? "#b45309" : "var(--text-2)" }}
        >
          {row.active ? STR.deactivate : STR.activate}
        </button>
        {row.invite_status === "invited" && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              void resendInvite();
            }}
            disabled={resending}
            title="Send a fresh invite link — the previous one may have expired (7 days)"
          >
            {resent ? "Invite sent" : resending ? "Sending…" : "Resend invite"}
          </button>
        )}
      </div>
    </div>
  );
}

function MiniAvatar({ row, size }: { row: EmployeeAdmin; size: number }) {
  const displayName = row.full_name || row.email || "?";
  if (row.photo_url) {
    return (
      <ZoomCropImage
        src={row.photo_url}
        alt={displayName}
        size={size}
        focalX={row.photo_focal_x}
        focalY={row.photo_focal_y}
        zoom={row.photo_zoom}
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "var(--surface-2)",
        border: "1px solid var(--line)",
        display: "grid",
        placeItems: "center",
        flex: "none",
        color: "var(--text)",
        fontWeight: 700,
        fontSize: size * 0.34,
      }}
    >
      {initials(displayName)}
    </div>
  );
}

/* ============================================================
   PhotoUploader — file picker that posts multipart to the Pages
   Function at /api/admin/photo-upload, which writes to R2 with
   the bucket binding and returns the public URL.
   ============================================================ */
function PhotoUploader({
  slug,
  currentUrl,
  onUploaded,
  onClear,
}: {
  slug: string;
  currentUrl: string;
  onUploaded: (url: string) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("slug", slug);
      const resp = await fetch("/api/admin/photo-upload", {
        method: "POST",
        body: fd,
        credentials: "same-origin",
      });
      const text = await resp.text();
      if (!resp.ok) {
        let msg = text;
        try {
          msg = (JSON.parse(text) as { error?: string }).error ?? text;
        } catch {
          /* leave text as-is */
        }
        throw new Error(`HTTP ${resp.status}: ${msg}`);
      }
      const data = JSON.parse(text) as { url: string };
      onUploaded(data.url);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
          }}
          style={{ display: "none" }}
        />
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {I.share({ size: 15 })}
          {uploading ? "Uploading…" : currentUrl ? "Replace photo" : "Upload photo"}
        </button>
        {currentUrl && !uploading && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClear}
            style={{ color: "#b45309" }}
            title="Remove the photo (the file stays in R2 but won't be referenced)"
          >
            Remove
          </button>
        )}
        <span
          className="mono"
          style={{ fontSize: 10.5, color: "var(--text-3)", letterSpacing: ".08em" }}
        >
          JPG · PNG · WEBP · ≤ 5 MB
        </span>
      </div>
      {error && (
        <div
          style={{
            color: "#b91c1c",
            fontSize: 12,
            border: "1px solid #fecaca",
            background: "#fef2f2",
            borderRadius: "var(--r-sm)",
            padding: 8,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

/* Shared zoom+crop avatar — background-image so both X and Y focal axes
   work at zoom > 100 (transform-origin + object-position interactions
   are too finicky to debug across browsers). Aspect detection picks the
   right backgroundSize axis so cover-fit holds at zoom = 100 for any
   orientation. */
function useImageOrientation(src: string): "portrait" | "landscape" | "square" | "loading" {
  const [o, setO] = useState<"portrait" | "landscape" | "square" | "loading">("loading");
  useEffect(() => {
    if (!src) {
      setO("loading");
      return;
    }
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth > img.naturalHeight) setO("landscape");
      else if (img.naturalWidth < img.naturalHeight) setO("portrait");
      else setO("square");
    };
    img.onerror = () => setO("portrait");
    img.src = src;
  }, [src]);
  return o;
}

function ZoomCropImage({
  src,
  size,
  focalX,
  focalY,
  zoom,
  ring = true,
}: {
  src: string;
  alt: string;
  size: number;
  focalX: number;
  focalY: number;
  zoom: number;
  ring?: boolean;
}) {
  const orientation = useImageOrientation(src);
  const bgSize =
    zoom <= 100
      ? "cover"
      : orientation === "landscape"
        ? `auto ${zoom}%`
        : `${zoom}% auto`;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        overflow: "hidden",
        border: ring ? "1px solid var(--line)" : "none",
        background: "var(--surface-2)",
        backgroundImage: safeBackgroundImage(src),
        backgroundSize: bgSize,
        backgroundPosition: `${focalX}% ${focalY}%`,
        backgroundRepeat: "no-repeat",
        flex: "none",
      }}
    />
  );
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ============================================================
   Add / Edit modal — form on the left, live card preview right.
   ============================================================ */
type FormState = {
  slug: string;
  short_code: string;
  full_name: string;
  title: string;
  company: string;
  phone: string;
  whatsapp_number: string;
  email: string;
  linkedin_url: string;
  photo_url: string;
  photo_focal_x: number;
  photo_focal_y: number;
  photo_zoom: number;
  prefilled_text: string;
  email_intro_subject: string;
  email_intro_body: string;
  active: boolean;
};

const DEFAULT_EMAIL_SUBJECT = "Great meeting you — {{my_first_name}} @ {{my_company}}";
const DEFAULT_EMAIL_BODY = `Hi {{first_name}},

Great to meet you. I'm with Sahab Systems — a Saudi engineering and
digital solutions company. I wanted to send along my contact details
so it's easy to follow up.

Looking forward to staying in touch.

— {{my_name}}`;

function emptyForm(): FormState {
  return {
    slug: "",
    short_code: "",
    full_name: "",
    title: "",
    company: "Sahab Systems",
    phone: "",
    whatsapp_number: "",
    email: "",
    linkedin_url: "",
    photo_url: "",
    photo_focal_x: 50,
    photo_focal_y: 25,
    photo_zoom: 100,
    prefilled_text: "",
    email_intro_subject: "",
    email_intro_body: "",
    active: true,
  };
}

function fromRow(r: EmployeeAdmin): FormState {
  return {
    slug: r.slug,
    short_code: r.short_code,
    full_name: r.full_name ?? "",
    title: r.title ?? "",
    company: r.company,
    phone: r.phone ?? "",
    whatsapp_number: r.whatsapp_number ?? "",
    email: r.email ?? "",
    linkedin_url: r.linkedin_url ?? "",
    photo_url: r.photo_url ?? "",
    photo_focal_x: r.photo_focal_x,
    photo_focal_y: r.photo_focal_y,
    photo_zoom: r.photo_zoom,
    prefilled_text: r.prefilled_text ?? "",
    email_intro_subject: r.email_intro_subject ?? "",
    email_intro_body: r.email_intro_body ?? "",
    active: r.active,
  };
}

function useIsNarrow(breakpoint = 720): boolean {
  const [narrow, setNarrow] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth < breakpoint,
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return narrow;
}

function EmployeeEditor({
  initial,
  onClose,
  onSaved,
}: {
  initial: EmployeeAdmin | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = initial === null;
  const isNarrow = useIsNarrow(720);
  const [form, setForm] = useState<FormState>(isNew ? emptyForm() : fromRow(initial));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function toPayload(): Record<string, unknown> {
    const obj: Record<string, unknown> = {};
    (Object.entries(form) as Array<[keyof FormState, unknown]>).forEach(([k, v]) => {
      if (typeof v === "string" && v.trim() === "") return;
      if (!isNew && k === "slug") return;
      if (!isNew && k === "short_code") return;
      if (isNew && k === "active") return;
      obj[k] = v;
    });
    if (!isNew && obj["active"] === undefined) obj["active"] = form.active;
    // Explicitly null photo_url when cleared — the empty-string skip above
    // would otherwise leave the old URL on the server.
    if (form.photo_url === "") obj["photo_url"] = null;
    // Always send focal coords on PATCH so the slider's "default-ish" values
    // (e.g. moving back to 50/25) overwrite a previously-set crop.
    obj["photo_focal_x"] = form.photo_focal_x;
    obj["photo_focal_y"] = form.photo_focal_y;
    obj["photo_zoom"] = form.photo_zoom;
    return obj;
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const payload = toPayload();
      if (isNew) {
        await adminApi.createEmployee(payload as Partial<EmployeeAdmin>);
      } else {
        await adminApi.updateEmployee(initial.id, payload as Partial<EmployeeAdmin>);
      }
      onSaved();
    } catch (err: unknown) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      setSubmitting(false);
    }
  }

  /* Build a stand-in EmployeePublic-shaped object for the live preview. */
  const previewEmp = useMemo(
    () => ({
      id: "preview",
      slug: form.slug || "preview",
      short_code: form.short_code || "SHB-####",
      full_name: form.full_name || "Full name",
      title: form.title || null,
      company: form.company || "Sahab Systems",
      department: null,
      phone: form.phone || null,
      email: form.email || null,
      address: null,
      linkedin_url: form.linkedin_url || null,
      website_url: null,
      photo_url: form.photo_url || null,
      photo_focal_x: form.photo_focal_x,
      photo_focal_y: form.photo_focal_y,
      photo_zoom: form.photo_zoom,
      whatsapp_number: form.whatsapp_number || null,
      prefilled_text: form.prefilled_text || `Hi! [c:${form.slug || "—"}]`,
      locale: "en",
    }),
    [form],
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60 }}>
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(6,9,15,.55)",
          backdropFilter: "blur(2px)",
          animation: "fadein .2s",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          padding: 20,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            pointerEvents: "auto",
            width: isNarrow ? "100%" : "min(880px, 96vw)",
            maxWidth: "100%",
            // 100dvh uses dynamic viewport height so iOS Safari's
            // collapsing address bar doesn't push the sticky footer
            // off-screen. Fixed height (not auto) on desktop too so the
            // inner overflowY:auto column has a constrained box to
            // scroll inside — otherwise tall forms overflow the modal
            // and the page itself instead of scrolling internally.
            maxHeight: isNarrow ? "100dvh" : "92vh",
            height: isNarrow ? "100dvh" : "92vh",
            background: "var(--field)",
            border: isNarrow ? "none" : "1px solid var(--line)",
            borderRadius: isNarrow ? 0 : "var(--r-xl)",
            boxShadow: "var(--elev)",
            overflow: "hidden",
            display: "grid",
            gridTemplateColumns: isNarrow ? "1fr" : "1fr 320px",
            // Ensure the modal child has well-defined height for flex
            // children to compute scrollable + sticky regions.
            minHeight: 0,
          }}
        >
          {/* form column — header + scrollable body + sticky footer
              so Save / Cancel stay visible no matter how long the form. */}
          <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "22px 28px 14px",
                borderBottom: "1px solid var(--line-2)",
                flex: "none",
              }}
            >
              <div>
                <div
                  className="mono"
                  style={{ fontSize: 10.5, letterSpacing: ".18em", color: "var(--text-3)" }}
                >
                  {form.short_code || "NEW · SHB-####"}
                </div>
                <h2 style={{ margin: "5px 0 0", fontSize: 21, fontWeight: 700, letterSpacing: "-.02em" }}>
                  {isNew ? STR.addEmployee : STR.edit}
                </h2>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ width: 38, height: 38, padding: 0 }}
                onClick={onClose}
              >
                {I.x({ size: 18 })}
              </button>
            </div>

            <div
              style={{
                padding: "16px 28px",
                overflowY: "auto",
                flex: 1,
                minHeight: 0,
              }}
            >
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Field label={STR.slug} wide={false}>
                <input
                  className="inp"
                  value={form.slug}
                  disabled={!isNew}
                  onChange={(e) => set("slug", e.target.value.toLowerCase())}
                  pattern="[a-z0-9-]+"
                  placeholder="jafar"
                />
              </Field>
              <Field label={STR.shortCode}>
                <input
                  className="inp"
                  value={form.short_code}
                  disabled={!isNew}
                  onChange={(e) => set("short_code", e.target.value.toUpperCase())}
                  pattern="[A-Z0-9]+"
                  maxLength={16}
                  placeholder="JFR01"
                />
              </Field>
              <Field label={STR.name} wide>
                <input
                  className="inp"
                  value={form.full_name}
                  placeholder="Set by the employee during onboarding"
                  disabled
                  readOnly
                />
              </Field>
              <Field label={`${STR.title} *`}>
                <input
                  className="inp"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  required
                />
              </Field>
              <Field label={STR.company}>
                <input
                  className="inp"
                  value={form.company}
                  onChange={(e) => set("company", e.target.value)}
                />
              </Field>
              <Field label={STR.mobile}>
                <input
                  className="inp"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="+9665…"
                />
              </Field>
              <Field label={STR.whatsapp}>
                <input
                  className="inp"
                  value={form.whatsapp_number}
                  onChange={(e) => set("whatsapp_number", e.target.value)}
                  placeholder="+9665…"
                />
              </Field>
              <Field label={`${STR.email} *`} wide>
                <input
                  className="inp"
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  required
                  placeholder="name@sahabsystems.com"
                />
              </Field>
              <Field label={STR.linkedin} wide>
                <input
                  className="inp"
                  value={form.linkedin_url}
                  onChange={(e) => set("linkedin_url", e.target.value)}
                  placeholder="https://www.linkedin.com/in/… (or leave blank — the employee can add this themselves)"
                />
              </Field>
              <Field label="Photo" wide>
                <PhotoUploader
                  slug={form.slug || "employee"}
                  currentUrl={form.photo_url}
                  onUploaded={(url) => {
                    set("photo_url", url);
                    // New photo → reset crop to portrait defaults so the
                    // admin can re-dial it for the new aspect.
                    set("photo_focal_x", 50);
                    set("photo_focal_y", 25);
                    set("photo_zoom", 100);
                  }}
                  onClear={() => set("photo_url", "")}
                />
                <input
                  className="inp"
                  value={form.photo_url}
                  onChange={(e) => set("photo_url", e.target.value)}
                  placeholder="…or paste an external URL"
                  style={{ marginTop: 8, fontSize: 12 }}
                />
              </Field>
              {form.photo_url && (
                <Field label="Photo crop — drag to frame the face" wide>
                  <FocalControls
                    x={form.photo_focal_x}
                    y={form.photo_focal_y}
                    zoom={form.photo_zoom}
                    onChange={(x, y, zoom) => {
                      set("photo_focal_x", x);
                      set("photo_focal_y", y);
                      set("photo_zoom", zoom);
                    }}
                  />
                </Field>
              )}
              <Field label={STR.prefilled} wide>
                <input
                  className="inp"
                  value={form.prefilled_text}
                  onChange={(e) => set("prefilled_text", e.target.value)}
                  placeholder={`Hi! [c:${form.slug || "<slug>"}]`}
                />
              </Field>
              <Field label="Card-intro email — Subject" wide>
                <input
                  className="inp"
                  value={form.email_intro_subject}
                  onChange={(e) => set("email_intro_subject", e.target.value)}
                  placeholder={DEFAULT_EMAIL_SUBJECT}
                />
                <span
                  className="mono"
                  style={{
                    fontSize: 10,
                    color: "var(--text-3)",
                    letterSpacing: ".06em",
                    marginTop: 4,
                  }}
                >
                  Vars: {"{{my_first_name}} {{my_company}} {{first_name}}"}. Empty
                  → use default.
                </span>
              </Field>
              <Field label="Card-intro email — Body" wide>
                <textarea
                  className="inp"
                  rows={9}
                  value={form.email_intro_body}
                  onChange={(e) => set("email_intro_body", e.target.value)}
                  placeholder={DEFAULT_EMAIL_BODY}
                  style={{
                    fontFamily: "var(--font-sans)",
                    lineHeight: 1.5,
                    resize: "vertical",
                  }}
                />
                <span
                  className="mono"
                  style={{
                    fontSize: 10,
                    color: "var(--text-3)",
                    letterSpacing: ".06em",
                    marginTop: 4,
                  }}
                >
                  Vars: {"{{first_name}} {{full_name}} {{company}} {{my_name}}"}.
                  Blank line = paragraph break. Photo + contact rows are added
                  automatically; just write the greeting + intro + sign-off.
                </span>
                {!isNew && initial && (
                  <SendTestEmailButton employeeId={initial.id} />
                )}
              </Field>
              {!isNew && (
                <Field label="Visibility" wide>
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 13.5,
                      color: "var(--text-2)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => set("active", e.target.checked)}
                    />
                    {STR.active.toLowerCase()} — visible on the public card
                  </label>
                </Field>
              )}
            </div>

              {error && (
                <div
                  style={{
                    color: "#b91c1c",
                    fontSize: 13,
                    border: "1px solid #fecaca",
                    background: "#fef2f2",
                    borderRadius: "var(--r-sm)",
                    padding: 10,
                    marginTop: 16,
                  }}
                >
                  {error}
                </div>
              )}
            </div>

            {/* sticky footer — always visible regardless of form length.
                position: sticky inside the form column AND flex: none in
                the column flex layout gives a defense-in-depth pin to the
                bottom of the modal on any viewport. */}
            <div
              style={{
                padding: "12px 18px calc(12px + env(safe-area-inset-bottom)) 18px",
                borderTop: "1px solid var(--line)",
                background: "var(--field)",
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
                flex: "none",
                position: "sticky",
                bottom: 0,
                zIndex: 2,
              }}
            >
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                {STR.clear}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  void submit();
                }}
                disabled={submitting}
                style={{ flex: 1, maxWidth: 280 }}
              >
                {I.check({ size: 17 })}
                {submitting ? "Saving…" : STR.saveChanges}
              </button>
            </div>
          </div>

          {/* live preview side panel — hidden on narrow viewports
              (mobile keeps the form full-width with a usable save bar). */}
          <div
            style={{
              borderInlineStart: "1px solid var(--line)",
              background: "var(--backdrop)",
              display: isNarrow ? "none" : "flex",
              flexDirection: "column",
            }}
          >
            <div className="tlm" style={{ padding: "16px 18px 0", textAlign: "center" }}>
              {STR.livePreview}
            </div>
            <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
              <div
                style={{
                  transform: "scale(.62)",
                  transformOrigin: "top center",
                  width: "161%",
                  marginLeft: "-30.5%",
                }}
              >
                <MiniCardPreview emp={previewEmp} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SendTestEmailButton — admin previews the rendered template by
   sending it to an arbitrary address. Useful for iterating on
   subject/body without staging a real card scan.
   ============================================================ */
function SendTestEmailButton({ employeeId }: { employeeId: string }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  async function send() {
    if (!to.includes("@")) {
      setStatus({ kind: "err", msg: "Enter a valid email" });
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const r = await adminApi.sendTestEmail(employeeId, to);
      setStatus({ kind: "ok", msg: `Sent to ${r.to_email}` });
    } catch (e: unknown) {
      const msg =
        e instanceof HttpError
          ? `HTTP ${e.status}: ${e.message}`
          : e instanceof Error
            ? e.message
            : String(e);
      setStatus({ kind: "err", msg });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        marginTop: 10,
        padding: 10,
        background: "var(--surface)",
        border: "1px dashed var(--line)",
        borderRadius: "var(--r-sm)",
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <span className="tlm" style={{ flex: "none" }}>
        Send a test
      </span>
      <input
        className="inp"
        type="email"
        placeholder="you@example.com"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        style={{ flex: 1, minWidth: 180, fontSize: 13 }}
      />
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => void send()}
        disabled={busy}
      >
        {busy ? "Sending…" : "Send test"}
      </button>
      {status && (
        <div
          style={{
            flexBasis: "100%",
            fontSize: 12,
            color: status.kind === "ok" ? "#15803d" : "#b91c1c",
            marginTop: 4,
          }}
        >
          {status.msg}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   FocalControls — Zoom + X + Y sliders.  At zoom = 100% the crop
   matches object-fit:cover (only one axis has slack for portrait
   or landscape sources).  Above 100%, scale-transform creates
   extra slack on both axes so X actually slides.
   ============================================================ */
function FocalControls({
  x,
  y,
  zoom,
  onChange,
}: {
  x: number;
  y: number;
  zoom: number;
  onChange: (x: number, y: number, zoom: number) => void;
}) {
  const labelStyle = {
    fontSize: 10,
    color: "var(--text-3)",
    letterSpacing: ".14em",
  } as const;
  const valStyle = { fontSize: 12, color: "var(--text-2)", textAlign: "right" as const };
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "72px 1fr 46px",
        rowGap: 8,
        columnGap: 10,
        alignItems: "center",
      }}
    >
      <span className="mono" style={labelStyle}>
        ZOOM
      </span>
      <input
        type="range"
        min={100}
        max={300}
        step={5}
        value={zoom}
        onChange={(e) => onChange(x, y, Number(e.target.value))}
        style={{ width: "100%", accentColor: "var(--signal)" }}
      />
      <span className="mono tnum" style={valStyle}>
        {(zoom / 100).toFixed(2)}x
      </span>

      <span className="mono" style={labelStyle}>
        Y · TOP
      </span>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={y}
        onChange={(e) => onChange(x, Number(e.target.value), zoom)}
        style={{ width: "100%", accentColor: "var(--signal)" }}
      />
      <span className="mono tnum" style={valStyle}>
        {y}%
      </span>

      <span className="mono" style={labelStyle}>
        X · LEFT
      </span>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={x}
        onChange={(e) => onChange(Number(e.target.value), y, zoom)}
        style={{ width: "100%", accentColor: "var(--signal)" }}
      />
      <span className="mono tnum" style={valStyle}>
        {x}%
      </span>

      <span />
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        style={{ justifySelf: "start", padding: "6px 10px", fontSize: 12 }}
        onClick={() => onChange(50, 25, 100)}
      >
        Reset to portrait default
      </button>
      <span />
    </div>
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <label className="fld" style={wide ? { gridColumn: "1 / -1" } : undefined}>
      <span className="tlm">{label}</span>
      {children}
    </label>
  );
}

/* ============================================================
   MiniCardPreview — pared-down Layout C used inside the editor
   modal. Doesn't include the live QR / vCard download (preview).
   ============================================================ */
type PreviewEmp = {
  id: string;
  slug: string;
  short_code: string;
  full_name: string;
  title: string | null;
  company: string;
  photo_url: string | null;
  photo_focal_x: number;
  photo_focal_y: number;
  photo_zoom: number;
  phone: string | null;
  email: string | null;
  address: string | null;
  linkedin_url: string | null;
  website_url: string | null;
  whatsapp_number: string | null;
  prefilled_text: string | null;
};

function MiniCardPreview({ emp }: { emp: PreviewEmp }) {
  const wa = whatsappLink({
    ...emp,
    department: null,
    locale: "en",
    company_profile_url: null,
    company_linkedin_url: null,
  });
  return (
    <div
      style={{
        background: "var(--backdrop)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "22px 14px 40px",
      }}
    >
      <div style={{ width: "100%", maxWidth: "var(--maxw)" }}>
        <div
          style={{
            background: "var(--field)",
            borderRadius: "var(--r-xl)",
            border: "1px solid var(--line)",
            boxShadow: "var(--elev)",
            overflow: "hidden",
          }}
        >
          <div style={{ position: "relative", overflow: "hidden" }}>
            <div
              style={{
                position: "relative",
                padding: 26,
                paddingTop: 24,
                display: "flex",
                flexDirection: "column",
                gap: 18,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 130,
                  left: "50%",
                  transform: "translateX(-50%)",
                  opacity: 0.06,
                  pointerEvents: "none",
                  color: "var(--text)",
                }}
              >
                <Mark size={440} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                <PreviewAvatar emp={emp} />
                <div style={{ textAlign: "center", maxWidth: 320 }}>
                  <h1
                    style={{
                      margin: 0,
                      fontSize: 25,
                      fontWeight: 800,
                      letterSpacing: "-.025em",
                      lineHeight: 1.22,
                    }}
                  >
                    {emp.full_name}
                  </h1>
                  {emp.title && (
                    <div
                      style={{
                        fontSize: 15,
                        color: "var(--text-2)",
                        fontWeight: 500,
                        marginTop: 10,
                      }}
                    >
                      {emp.title}
                    </div>
                  )}
                  <div
                    className="mono"
                    style={{
                      fontSize: 10.5,
                      letterSpacing: ".22em",
                      color: "var(--text-3)",
                      marginTop: 10,
                    }}
                  >
                    {emp.company.toUpperCase()} · {emp.short_code}
                  </div>
                </div>
              </div>
              <div
                className="btn btn-primary"
                style={{
                  width: "100%",
                  padding: "16px 18px",
                  fontSize: 16,
                  borderRadius: "var(--r-md)",
                  opacity: wa ? 1 : 0.4,
                }}
              >
                {I.whatsapp({ size: 20 })}
                <span>Chat on WhatsApp</span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  border: "1px solid var(--line)",
                  borderRadius: "var(--r-lg)",
                  padding: 16,
                  background: "var(--surface)",
                }}
              >
                <div
                  style={{
                    background: "var(--field)",
                    border: "1px solid var(--line)",
                    borderRadius: "var(--r-md)",
                    padding: 8,
                    flex: "none",
                  }}
                >
                  <SahabQR url={vcardUrl(emp.slug)} size={84} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <div className="tlm">Scan to share this card</div>
                  <div
                    className="mono"
                    style={{ fontSize: 10.5, letterSpacing: ".18em", color: "var(--text-3)" }}
                  >
                    {emp.short_code}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewAvatar({ emp }: { emp: PreviewEmp }) {
  if (emp.photo_url) {
    return (
      <ZoomCropImage
        src={emp.photo_url}
        alt={emp.full_name}
        size={150}
        focalX={emp.photo_focal_x}
        focalY={emp.photo_focal_y}
        zoom={emp.photo_zoom}
      />
    );
  }
  return (
    <div
      style={{
        width: 150,
        height: 150,
        borderRadius: "50%",
        background: "var(--surface-2)",
        border: "1px solid var(--line)",
        display: "grid",
        placeItems: "center",
        fontWeight: 700,
        fontSize: 50,
        color: "var(--text)",
      }}
    >
      {initials(emp.full_name)}
    </div>
  );
}
